import type { IQuestionSection } from '../types/meetData.type'

export type PanelQuestion = {
  id: string
  text: string
  asked: boolean
  answered: boolean
  generated?: boolean
}

export type PanelSkill = {
  id: string
  name: string
  questions: PanelQuestion[]
}

export type QuestionCatalogStateItem = {
  id: string
  text: string
  skillId?: string
  skillName?: string
  answered?: boolean
  generated?: boolean
}

export type QuestionCatalogStateMessage = {
  action: 'question_catalog_state'
  sessionId?: string
  questions: QuestionCatalogStateItem[]
}

export type QuestionCatalogRequestMessage = {
  action: 'request_question_catalog'
  sessionId?: string
}

const SKILL_SECTION_HEADER = /^навык:\s*(.+)$/i

export const buildQuestionSessionId = (
  participantIdentity: string,
  participantSid: string,
  timestamp: number,
) => `${participantIdentity}:${participantSid}:${timestamp}`

const toSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/[^a-zа-я0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')

export const buildPanelSkills = (
  sections: Array<{ name: string; questions: string[] }>,
): PanelSkill[] =>
  sections.flatMap((section, sectionIndex) => {
    const name = String(section.name || '').trim() || 'Общие вопросы'
    const questions = (section.questions || [])
      .map((question) => String(question || '').trim())
      .filter(Boolean)

    if (questions.length === 0) {
      return []
    }

    const sectionSlug = toSlug(name) || 'section'

    return [
      {
        id: `${sectionSlug}-${sectionIndex + 1}`,
        name,
        questions: questions.map((question, questionIndex) => ({
          id: `${sectionSlug}-question-${questionIndex + 1}`,
          text: question,
          asked: false,
          answered: false,
        })),
      },
    ]
  })

export const buildSectionsFromCatalog = (
  sections: IQuestionSection[],
): PanelSkill[] =>
  buildPanelSkills(
    sections.map((section) => {
      const header = String(section.header || '').trim()
      const name =
        header.match(SKILL_SECTION_HEADER)?.[1]?.trim() || header || 'Общие вопросы'

      return { name, questions: section.questions || [] }
    }),
  )

export const resolveAskedQuestionId = (
  topic: string | undefined,
  payload: string,
  currentSessionId?: string,
): string | null => {
  if (!topic?.includes('lk-question-spoken-topic')) {
    return null
  }

  try {
    const spokenQuestion = JSON.parse(payload) as {
      questionId?: unknown
      confidence?: unknown
      sessionId?: unknown
    }
    const eventSessionId =
      typeof spokenQuestion.sessionId === 'string' ? spokenQuestion.sessionId.trim() : ''
    if (eventSessionId && currentSessionId && eventSessionId !== currentSessionId) {
      return null
    }

    const questionId =
      typeof spokenQuestion.questionId === 'string' ? spokenQuestion.questionId.trim() : ''

    return questionId && spokenQuestion.confidence !== 'low' ? questionId : null
  } catch {
    return null
  }
}

export const isQuestionCatalogRequest = (
  topic: string | undefined,
  payload: string,
): boolean => {
  if (!topic?.includes('lk-question-control-topic')) {
    return false
  }

  try {
    const message = JSON.parse(payload) as { action?: unknown }
    return message.action === 'request_question_catalog'
  } catch {
    return false
  }
}

export const parseQuestionCatalogRequest = (
  participantIdentity: string | undefined,
  topic: string | undefined,
  payload: string,
): QuestionCatalogRequestMessage | null => {
  if (
    !topic?.includes('lk-question-control-topic') ||
    (participantIdentity && !participantIdentity.startsWith('agent-'))
  ) {
    return null
  }

  try {
    const message = JSON.parse(payload) as QuestionCatalogRequestMessage
    if (message.action !== 'request_question_catalog') {
      return null
    }

    return {
      action: message.action,
      ...(typeof message.sessionId === 'string' && message.sessionId.trim()
        ? { sessionId: message.sessionId.trim() }
        : {}),
    }
  } catch {
    return null
  }
}

export const shouldHandleQuestionCatalogRequest = (
  participantIdentity: string | undefined,
  topic: string | undefined,
  payload: string,
): boolean =>
  Boolean(parseQuestionCatalogRequest(participantIdentity, topic, payload))

export const parseQuestionCatalogState = (
  participantIdentity: string | undefined,
  topic: string | undefined,
  payload: string,
): QuestionCatalogStateMessage | null => {
  if (
    !topic?.includes('lk-question-control-topic') ||
    (participantIdentity && !participantIdentity.startsWith('agent-'))
  ) {
    return null
  }

  try {
    const message = JSON.parse(payload) as QuestionCatalogStateMessage
    if (message.action !== 'question_catalog_state' || !Array.isArray(message.questions)) {
      return null
    }

    return message
  } catch {
    return null
  }
}

export const applyQuestionCatalogState = (
  currentSkills: PanelSkill[],
  catalog: QuestionCatalogStateItem[],
): PanelSkill[] => {
  const existingQuestions = new Map(
    currentSkills.flatMap((skill) =>
      skill.questions.map((question) => [question.id, { skill, question }] as const),
    ),
  )
  const skillOrder: string[] = []
  const skillsById = new Map<string, PanelSkill>()

  catalog
    .filter((item) => String(item?.id || '').trim() && String(item?.text || '').trim())
    .forEach((item, index) => {
      const existing = existingQuestions.get(item.id)
      const skillId = String(item.skillId || existing?.skill.id || `section-${index + 1}`)
      const skillName = String(item.skillName || existing?.skill.name || 'Общие вопросы')

      if (!skillsById.has(skillId)) {
        skillOrder.push(skillId)
        skillsById.set(skillId, { id: skillId, name: skillName, questions: [] })
      }

      const generated = item.generated ?? existing?.question.generated
      skillsById.get(skillId)?.questions.push({
        id: String(item.id),
        text: String(item.text || existing?.question.text || ''),
        asked: Boolean(item.answered),
        answered: Boolean(item.answered),
        ...(generated !== undefined ? { generated } : {}),
      })
    })

  return skillOrder.map((skillId) => skillsById.get(skillId) as PanelSkill)
}
